import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
} from "@mui/material";
import axios from "axios";
import { getErrorMessage } from "utils/errorHelpers";
import { ZUORA_KEY_REGEX } from "./zuoraKeyValidation";

interface EditZuoraSubscriptionKeyDialogProps {
  open: boolean;
  subscriptionName: string;
  currentZuoraSubscriptionKey?: string | null;
  handleClose: () => void;
  submitChange: (zuoraSubscriptionKey: string | null) => Promise<void>;
}

export function EditZuoraSubscriptionKeyDialog({
  open,
  subscriptionName,
  currentZuoraSubscriptionKey,
  handleClose,
  submitChange,
}: EditZuoraSubscriptionKeyDialogProps) {
  const [zuoraSubscriptionKey, setZuoraSubscriptionKey] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setZuoraSubscriptionKey(currentZuoraSubscriptionKey ?? "");
      setSubmitError(null);
    }
  }, [open, currentZuoraSubscriptionKey]);

  const trimmedValue = zuoraSubscriptionKey.trim();
  const isClearing = trimmedValue === "";
  const isValid = isClearing || ZUORA_KEY_REGEX.test(trimmedValue);

  const handleSubmit = async () => {
    if (!isValid) return;

    setIsSubmitting(true);
    try {
      await submitChange(isClearing ? null : trimmedValue);
      handleClose();
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        setSubmitError("This Zuora subscription key is already in use by another subscription.");
      } else {
        setSubmitError(getErrorMessage(error));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Zuora Subscription Details</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
            }}
          >
            Subscription: <strong>{subscriptionName}</strong>
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
            }}
          >
            Current Key: <strong>{currentZuoraSubscriptionKey || "Not set"}</strong>
          </Typography>
        </Box>

        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        )}

        <TextField
          label="Zuora Subscription Key"
          value={zuoraSubscriptionKey}
          onChange={(e) => {
            setZuoraSubscriptionKey(e.target.value);
            setSubmitError(null);
          }}
          error={!isValid}
          helperText={
            !isValid
              ? "Must start with 'SUB' followed by at least 8 digits (e.g., SUB12345678)"
              : "Leave empty to clear the key"
          }
          fullWidth
          placeholder="SUB12345678"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" disabled={isSubmitting || !isValid}>
          {isSubmitting ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
